import React, { useEffect, useRef, useState } from 'react';
import { OrderProvider, useOrder } from './src/state/OrderContext';
import { QueueProvider, useQueue } from './src/state/QueueContext';
import { HistoryProvider, useHistory } from './src/state/HistoryContext';
import { RecipeLibraryProvider, useRecipeLibrary } from './src/state/RecipeLibraryContext';
import { LocationProvider } from './src/state/LocationContext';
import { readSharedInput, stripConsumed } from './src/lib/readSharedInput';
import { TRIP_PICKED_UP } from './src/lib/pickupTrip';
import Home from './src/screens/Home';
import Recipe from './src/screens/Recipe';
import Queue from './src/screens/Queue';
import History from './src/screens/History';
import './src/styles/theme.css';

// Four screens, no staged flow. Home turns an idea into a recipe; Recipe holds
// the meal, its editable ingredients and the nearby shops. Queue and History
// are side destinations off Home — both feed a meal back into Home's input.

function AppBody() {
  const [view, setView] = useState('home');
  // Bumped whenever Queue or History hands a meal back, so Home knows to run
  // the search itself rather than waiting for the user to ask again.
  const [seedToken, setSeedToken] = useState(0);
  const { recipe, store, trip, update, reset, selectRecipe } = useOrder();
  const { takeItem } = useQueue();
  const { addEntry } = useHistory();
  const { getRecipe } = useRecipeLibrary();

  // Recipe is only reachable once one has actually been selected.
  const current = view === 'recipe' && !recipe ? 'home' : view;

  function seedHome(input) {
    reset();
    update({ input });
    setSeedToken((n) => n + 1);
    setView('home');
  }

  // Restoring a meal we already hold — the recipe itself, not its name.
  //
  // This is the difference between "cook again" meaning something and it being
  // a search box that happens to be pre-filled. A history entry stores a
  // `recipeId`; if the library still has that recipe, the user gets back the
  // ingredients, the steps and the servings they actually cooked, and lands on
  // the recipe rather than on Home to re-derive it. Re-deriving could return
  // something different — the online mock has no obligation to be stable — so
  // the same name is not the same meal.
  //
  // Returns false when there is no recipe behind the entry, which is a real
  // case: a queued idea that was never resolved has `recipeId: null`. The
  // caller falls back to seeding Home, which is the honest thing to do with a
  // name and nothing else.
  // `match` is display-only. Left unset it describes the recipe by how we hold
  // it; History passes its own, because "cooked on the 1st" is the more useful
  // thing to say about a meal you are cooking again.
  function openStoredRecipe(recipeId, match = null) {
    const stored = recipeId ? getRecipe(recipeId) : null;
    if (!stored) return false;
    reset();
    selectRecipe(
      stored,
      stored.dishName,
      match ?? { kind: stored.savedAt ? 'saved' : 'library', savedAt: stored.savedAt ?? null, lastCookedAt: null }
    );
    setView('recipe');
    return true;
  }

  // Something shared in from another app, which reaches us as query parameters
  // and nothing else — see lib/readSharedInput for what that does and does not
  // cover. The ref is what stops a double-invoked effect ingesting twice.
  const sharedRef = useRef(false);
  useEffect(() => {
    if (sharedRef.current) return;
    sharedRef.current = true;

    const { value, consumed } = readSharedInput(window.location.search);
    if (consumed.length === 0) return;

    // Read the value out first, then rewrite the URL. Doing it the other way
    // round would throw away the thing we were asked to act on.
    const remaining = stripConsumed(window.location.search, consumed);
    window.history.replaceState({}, '', `${window.location.pathname}${remaining}${window.location.hash}`);

    if (value) seedHome(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The recipe id is what makes this entry reusable later — without it, cooking
  // the same meal again would re-derive a recipe instead of returning this one.
  function markAsMade() {
    // Where they actually shopped beats where they were going to: if a pickup
    // was marked, that store is the one that happened, even if the selection
    // has since moved on. Falling back to the current store keeps an entry
    // useful for someone who never touched the pickup buttons at all.
    const shoppedAt = trip?.status === TRIP_PICKED_UP ? (trip.storeName ?? store?.name) : store?.name;
    addEntry({ mealName: recipe.dishName, store: shoppedAt, recipeId: recipe.id });
    reset();
    setView('home');
  }

  switch (current) {
    case 'recipe':
      return <Recipe onBack={() => setView('home')} onDone={markAsMade} />;
    case 'queue':
      return (
        <Queue
          onBack={() => setView('home')}
          // "Start this one" restores the queued recipe if there is one, and
          // otherwise the original input — a link goes back through full route
          // resolution and is extracted, not searched for by name.
          onStartItem={(id) => {
            const item = takeItem(id);
            if (!item) return;
            if (openStoredRecipe(item.recipeId)) return;
            seedHome(item.sourceInput);
          }}
        />
      );
    case 'history':
      return (
        <History
          onBack={() => setView('home')}
          // The full recipe as cooked, not a fresh search for its name.
          onCookAgain={(entry) => {
            if (
              openStoredRecipe(entry.recipeId, {
                kind: 'cooked',
                lastCookedAt: entry.completedAt,
              })
            ) {
              return;
            }
            seedHome(entry.mealName);
          }}
        />
      );
    case 'home':
    default:
      return (
        <Home
          seedToken={seedToken}
          onOpenRecipe={() => setView('recipe')}
          onOpenQueue={() => setView('queue')}
          onOpenHistory={() => setView('history')}
        />
      );
  }
}

export default function App() {
  return (
    <OrderProvider>
      <QueueProvider>
        <HistoryProvider>
          <RecipeLibraryProvider>
            {/* Location is a provider so the answer survives moving between
                screens, NOT so it can be collected in the background — it is
                only ever asked for when the store section needs it. */}
            <LocationProvider>
              <AppBody />
            </LocationProvider>
          </RecipeLibraryProvider>
        </HistoryProvider>
      </QueueProvider>
    </OrderProvider>
  );
}
